<template>
  <div :class="getItemTableContainerClass">
    <ItemTable
      ref="itemTable"
      :boundItemsUrl="boundItemsUrl"
      :collectionFilters="getCollectionFilters"
      :columns="columns"
      :details="inMainPanel"
      :gettingStartedItemType="itemType"
      :handleBulkDelete="handleBulkDelete"
      :handleDetailsButtonClick="handleDetailsButtonClick"
      :handleMainAction="handleMainAction"
      :handleRowClick="handleRowClick"
      :hasSelectColumn="true"
      :inDetailsAsidePanel="!inMainPanel"
      :isAsideOpen="isAsideOpen"
      :itemType="itemType"
      :keepFresh="inMainPanel"
      :route="route"
      :selectedModelId="selectedModelId"
      :selectionItemText="selectionItemText"
      :setSelectedModelId="setSelectedModelId"
      :showBound="false"
      :showBulkDelete="inMainPanel"
      :showMainAction="inMainPanel"
      :showSearchForm="true"
      :viewport="viewport"
    />
    <AlertContainer>
      <DeletedItemsNotifications
        :displayName="notificationDisplayName"
        :itemType="itemType"
      />
    </AlertContainer>
  </div>
</template>

<script>
import {
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeDisplayNames,
  ItemTypeRoutes,
  StoreModuleMap,
} from 'client/util/Constants';
import { mapActions, mapGetters } from 'vuex';
import AlertContainer from 'client/components/Alert/AlertContainer';
import DeletedItemsNotifications from 'client/components/ItemTable/DeleteItemsNotifications.vue';
import ItemTable from 'client/components/ItemTable/ItemTable.vue';
import ItemTableComputedMixin from 'client/components/ItemTable/mixins/ItemTableComputedMixin';
import ItemTableIndirectBindingMixin from 'client/components/ItemTable/mixins/ItemTableIndirectBindingMixin';
import ItemTableMethodsMixin from 'client/components/ItemTable/mixins/ItemTableMethodsMixin';
import ItemTablePropsMixin from 'client/components/ItemTable/mixins/ItemTablePropsMixin';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import Util from 'client/util/Util';
import pluralize from 'pluralize';

const softwareAppsCollection = StoreModuleMap[ItemTypeConsts.softwareApps];

export default {
  name: 'SoftwareAppsItemTable',

  components: {
    AlertContainer,
    ItemTable,
    DeletedItemsNotifications,
  },

  mixins: [
    ItemTableComputedMixin,
    ItemTableIndirectBindingMixin,
    ItemTableMethodsMixin,
    ItemTablePropsMixin,
  ],

  props: {
    asidePanelCanceledEventName: {
      default: 'softwareApp-details:canceled',
      type: String,
    },
    panelType: {
      default: ItemTableConsts.panelType.mainPanel,
      type: String,
    },
  },

  data: () => ({
    itemType: ItemTypeConsts.softwareApps,
    route: `/${ItemTypeRoutes[ItemTypeConsts.softwareApps]}`,
    columns: [
      {
        dataField: 'displayName',
        headingText: 'Name',
        sortBy: 'displayName',
      },
      {
        bodyComponent: 'ItemName',
        bodyComponentProps: {
          titleField: 'settings[0].packageId',
        },
        headingText: 'Package ID',
      },
    ],
  }),

  computed: {
    ...mapGetters(softwareAppsCollection, {
      selectedItems: 'getSelectedItems',
    }),

    SoftwareAppsNoun() {
      return (this.SoftwareAppsNounCap).toLowerCase();
    },

    SoftwareAppsNounCap() {
      return ItemTypeDisplayNames[this.itemType];
    },

    deleteModalBodyText() {
      const baseText = `**Are you sure** you would like to delete the selected ${this.SoftwareAppsNoun}? If you do, you won’t be able to deploy ${pluralize('this', this.selectedItems.length)} ${pluralize(this.notificationDisplayName, this.selectedItems.length)} using JumpCloud. On devices where you deployed ${pluralize('this', this.selectedItems.length)} ${pluralize(this.notificationDisplayName, this.selectedItems.length)}`;
      return (this.selectedItems.length > 1)
        ? `${baseText}, they remain and are not uninstalled.`
        : `${baseText}, it remains and is not uninstalled.`;
    },

    getCollectionFilters() {
      return [{ name: 'packageManager', operator: 'eq', value: 'CHOCOLATEY' }];
    },

    notificationDisplayName() {
      return `${this.SoftwareAppsNoun} package`;
    },

  },

  methods: {
    ...mapActions(softwareAppsCollection, [
      'deleteItems',
      'setDeletedItemIds',
    ]),

    handleBulkDelete() {
      const {
        deleteItems, deleteModalBodyText, notifyDeleteItems, selectedItems, SoftwareAppsNounCap,
      } = this;

      const selectedCount = selectedItems.length;

      if (!selectedCount) {
        return;
      }

      ModalEventBus.open({
        isLargeModal: false,
        component: 'ConfirmationModal',
        componentProps: {
          handleContinue: () => deleteItems(selectedItems)
            .then(res => notifyDeleteItems(res))
            .finally(() => {
              ModalEventBus.close();
            }),
          mainActionAttention: true,
          mdTemplate: deleteModalBodyText,
          kbLink: KbArticleUrls.softwareApplicationDelete,
          continueText: 'delete',
          headerText: `Delete ${SoftwareAppsNounCap}`,
        },
      });
    },

    handleDetailsButtonClick(itemId) {
      if (this.inMainPanel && this.viewport) {
        this.viewport.redirect(`${this.route}/windows/${itemId}/details`);
      } else {
        Util.redirect(`${this.route}/windows/${itemId}/details`);
      }
    },

    handleMainAction() {
      if (this.viewport) {
        this.viewport.redirect(`${this.route}/windows/new`);
      } else {
        Util.redirect(`${this.route}/windows/new`);
      }
    },

    // The deleted item ids set here will be picked up by the
    // DeletedItemsNotifications component which will show the notification toast
    notifyDeleteItems(res) {
      this.setDeletedItemIds({ deletedItemIds: res.deletedIds, failedTasks: res.failedTasks });
    },
  },
};
</script>
